import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faCheck } from "@fortawesome/free-solid-svg-icons"
import {
	postPointsInterets,
	postPointsInteretsReset,
	deletePointInteret,
	deletePointInteretReset,
} from "../../../store/actions/pointInteretActions"
import {
	POINT_INTERET_TYPE_PARC,
	POINT_INTERET_TYPE_FOURRIERE,
	POINT_INTERET_TYPE_POLICE,
	POINT_INTERET_TYPE_ATELIER,
} from "../../others/PointInteretComponent"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import CustomModal from "../../basics/interface/CustomModal"
import AdresseV2 from "../entite/AdresseV2"
import CoordonneesPointV2 from "./CoordonneesPointV2"

// Catégories gérées dans la Configuration (cf. ancienne page /options).
export const POI_TYPE_OPTIONS = [
	{ value: POINT_INTERET_TYPE_PARC, label: "Parc" },
	{ value: POINT_INTERET_TYPE_FOURRIERE, label: "Fourrière" },
	{ value: POINT_INTERET_TYPE_POLICE, label: "Parc police" },
	{ value: POINT_INTERET_TYPE_ATELIER, label: "Atelier" },
]

const styles = {
	adresseTitle: {
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		marginBottom: 14,
		marginTop: 4,
	},
}

// Édition d'un point d'intérêt (modal V2). POST/DELETE renvoient la liste
// fraîche (parcs/fourrieres) → le reducer met la table à jour seul.
const PointInteretModalV2 = ({ point, onClose }) => {
	const dispatch = useDispatch()
	const postState = useSelector((s) => s.pointInteretInfo.postPointsInterets)
	const deleteState = useSelector(
		(s) => s.pointInteretInfo.deletePointInteret,
	)
	const isCreate = !point.id
	const [confirmDelete, setConfirmDelete] = useState(false)
	const [form, setForm] = useState({
		id: point.id || null,
		label: point.label || "",
		type: point.type,
		adresse: point.adresse,
	})
	const update = (key, value) => setForm((p) => ({ ...p, [key]: value }))
	const updateAdresse = (key, value, whole) =>
		setForm((p) => ({
			...p,
			adresse: whole ? value : { ...p.adresse, [key]: value },
		}))

	// Fermeture après une sauvegarde OU une suppression réussie.
	useEffect(() => {
		if (!postState.saved && !deleteState.deleted) return
		const t = setTimeout(() => {
			dispatch(postPointsInteretsReset())
			dispatch(deletePointInteretReset())
			onClose()
		}, 800)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [postState.saved, deleteState.deleted])

	useEffect(
		() => () => {
			dispatch(postPointsInteretsReset())
			dispatch(deletePointInteretReset())
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	// Quitte le mode "confirmer" si une erreur de suppression survient.
	useEffect(() => {
		if (deleteState.error) setConfirmDelete(false)
	}, [deleteState.error])

	// GE-69 : un atelier exige rue, NPA et localité (le serveur refuse aussi
	// une adresse introuvable sans lien Maps).
	const estAtelier = form.type === POINT_INTERET_TYPE_ATELIER
	const adresseIncomplete = ["rue", "code_postal", "localite"].some(
		(c) => !String((form.adresse || {})[c] || "").trim(),
	)
	const invalid = !form.label.trim() || (estAtelier && adresseIncomplete)

	const save = () => dispatch(postPointsInterets(form))
	const remove = () => {
		if (!confirmDelete) {
			setConfirmDelete(true)
			return
		}
		dispatch(deletePointInteret({ id: point.id }))
	}

	const busy =
		postState.saving ||
		postState.saved ||
		deleteState.deleting ||
		deleteState.deleted

	const buttons = []
	if (!isCreate) {
		buttons.push({
			title: deleteState.deleting ? (
				<FontAwesomeIcon icon={faSync} spin />
			) : confirmDelete ? (
				"Confirmer ?"
			) : (
				"Supprimer"
			),
			styleName: "danger",
			onClick: remove,
			disabled: busy,
		})
	}
	buttons.push({
		title: "Annuler",
		styleName: "secondary",
		onClick: onClose,
		disabled: busy,
	})
	buttons.push({
		title: postState.saving ? (
			<FontAwesomeIcon icon={faSync} spin />
		) : postState.saved ? (
			<FontAwesomeIcon icon={faCheck} />
		) : (
			"Enregistrer"
		),
		styleName: "primary",
		onClick: save,
		disabled: busy || invalid,
	})

	return (
		<CustomModal
			title={
				isCreate
					? "Nouveau point d'intérêt"
					: "Éditer le point d'intérêt"
			}
			onClose={busy ? () => {} : onClose}
			showCloseButton={!busy}
			buttons={buttons}
		>
			<div style={gs.fieldGroup}>
				<div style={gs.fieldRow}>
					<label style={gs.label}>
						Nom<span style={{ color: COLORS.danger }}> *</span>
					</label>
					<input
						style={gs.input}
						value={form.label}
						onChange={(e) => update("label", e.target.value)}
					/>
				</div>
				<div style={gs.fieldRow}>
					<label style={gs.label}>Catégorie</label>
					<select
						style={gs.input}
						value={form.type}
						onChange={(e) =>
							update("type", parseInt(e.target.value))
						}
					>
						{POI_TYPE_OPTIONS.map((o) => (
							<option key={o.value} value={o.value}>
								{o.label}
							</option>
						))}
					</select>
				</div>
			</div>
			<div style={{ marginTop: 18 }}>
				<div style={styles.adresseTitle}>
					Adresse
					{estAtelier && (
						<span style={{ color: COLORS.danger }}> *</span>
					)}
				</div>
				<AdresseV2
					adresse={form.adresse}
					update={updateAdresse}
					showExtraData={form.type === POINT_INTERET_TYPE_PARC}
				/>
			</div>
			{/* GE-47 : coordonnées (géocodage auto, lien Maps prioritaire). */}
			<div style={{ marginTop: 18 }}>
				<div style={styles.adresseTitle}>Coordonnées</div>
				<CoordonneesPointV2
					adresse={form.adresse}
					onModifierAdresse={(fn) =>
						setForm((p) => ({ ...p, adresse: fn(p.adresse) }))
					}
				/>
			</div>
		</CustomModal>
	)
}

export default PointInteretModalV2
